import { useEffect, useState } from "react";
import { Mail, MessageCircle, Phone } from "lucide-react";
import Reveal from "@/components/Reveal";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api, imgUrl } from "@/lib/api";
import { useLang } from "@/i18n";

const FALLBACK_TEAM = [
  {
    id: "fallback-1",
    name: "Nelson Soares",
    role: "Founder & CEO",
    bio: "",
    photo: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=600&q=80",
  },
  {
    id: "fallback-2",
    name: "Ana Pereira",
    role: "Head of Cybersecurity",
    bio: "",
    photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80",
  },
  {
    id: "fallback-3",
    name: "Rui Gonçalves",
    role: "Lead Software Engineer",
    bio: "",
    photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=600&q=80",
  },
  {
    id: "fallback-4",
    name: "Maria da Costa",
    role: "ICT Support Manager",
    bio: "",
    photo: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=600&q=80",
  },
];

const waDigits = (phone) => (phone || "").replace(/[^0-9]/g, "");

export default function Team() {
  const { t } = useLang();
  const [members, setMembers] = useState(FALLBACK_TEAM);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api
      .get("/team")
      .then((r) => {
        if (r.data?.length) setMembers(r.data);
      })
      .catch(() => {});
  }, []);

  return (
    <section
      id="team"
      data-testid="team-section"
      className="bg-[#0B1120] px-6 md:px-12 lg:px-24 py-20 lg:py-32"
    >
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <Reveal>
          <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">
            {t("team_label")}
          </p>
          <h2 className="font-display font-semibold text-white text-4xl sm:text-5xl tracking-tight">
            {t("team_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-slate-400 max-w-sm text-sm lg:text-base">
            {t("team_sub")}
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {members.map((m, i) => (
          <Reveal key={m.id} delay={i * 0.08}>
            <button
              data-testid={`team-member-${i}`}
              onClick={() => setSelected(m)}
              className="group w-full text-left cursor-pointer"
            >
              <div className="relative aspect-[4/5] overflow-hidden border border-white/10 mb-4">
                {m.photo ? (
                  <img
                    src={imgUrl(m.photo)}
                    alt={m.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-700"
                  />
                ) : (
                  <div className="w-full h-full bg-[#0F172A] flex items-center justify-center">
                    <span className="font-display text-5xl text-slate-700">
                      {m.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                    </span>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#ED1C24] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                <div className="absolute inset-0 bg-[#0B1120]/0 group-hover:bg-[#0B1120]/25 transition-colors duration-500 flex items-end justify-center opacity-0 group-hover:opacity-100">
                  <span className="mb-4 text-[10px] tracking-[0.25em] uppercase text-white bg-[#ED1C24] px-4 py-2">
                    {t("team_view_profile")}
                  </span>
                </div>
              </div>
              <h3 className="font-display font-semibold text-white text-lg">{m.name}</h3>
              <p className="text-xs tracking-[0.15em] uppercase text-slate-500 mt-1 group-hover:text-[#FFC61A] transition-colors duration-300">
                {m.role}
              </p>
              {m.bio && (
                <p className="text-xs text-slate-500 leading-relaxed mt-3 line-clamp-3">{m.bio}</p>
              )}
            </button>
          </Reveal>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent data-testid="team-profile-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none sm:max-w-xl p-0 overflow-hidden">
          {selected && (
            <div className="flex flex-col sm:flex-row">
              <div className="sm:w-2/5 shrink-0">
                {selected.photo ? (
                  <img
                    src={imgUrl(selected.photo)}
                    alt={selected.name}
                    className="w-full h-56 sm:h-full object-cover object-top grayscale"
                  />
                ) : (
                  <div className="w-full h-56 sm:h-full bg-[#0B1120] flex items-center justify-center">
                    <span className="font-display text-6xl text-slate-700">
                      {selected.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-7 flex flex-col">
                <h3 className="font-display font-semibold text-white text-2xl leading-tight">
                  {selected.name}
                </h3>
                <p className="text-xs tracking-[0.2em] uppercase text-[#FFC61A] mt-2 mb-4">
                  {selected.role}
                </p>
                {selected.bio && (
                  <p className="text-sm text-slate-400 leading-relaxed mb-6">{selected.bio}</p>
                )}
                <div className="mt-auto space-y-2.5">
                  {selected.email && (
                    <a
                      data-testid="team-profile-email"
                      href={`mailto:${selected.email}`}
                      className="flex items-center gap-3 text-sm text-slate-300 hover:text-[#FFC61A] transition-colors"
                    >
                      <Mail size={15} className="text-[#ED1C24] shrink-0" /> {selected.email}
                    </a>
                  )}
                  {selected.phone && (
                    <a
                      data-testid="team-profile-phone"
                      href={`tel:${waDigits(selected.phone)}`}
                      className="flex items-center gap-3 text-sm text-slate-300 hover:text-[#FFC61A] transition-colors"
                    >
                      <Phone size={15} className="text-[#ED1C24] shrink-0" /> {selected.phone}
                    </a>
                  )}
                  {selected.phone && waDigits(selected.phone).length >= 7 && (
                    <a
                      data-testid="team-profile-whatsapp"
                      href={`https://wa.me/${waDigits(selected.phone)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 text-sm text-slate-300 hover:text-[#FFC61A] transition-colors"
                    >
                      <MessageCircle size={15} className="text-[#ED1C24] shrink-0" /> WhatsApp
                    </a>
                  )}
                  {!selected.email && !selected.phone && (
                    <a
                      data-testid="team-profile-general-contact"
                      href="mailto:service@kinos.tl"
                      className="flex items-center gap-3 text-sm text-slate-300 hover:text-[#FFC61A] transition-colors"
                    >
                      <Mail size={15} className="text-[#ED1C24] shrink-0" /> service@kinos.tl
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
